import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { and, gt, not, or } from 'tio-ui/utilities';
import { htmlSafe } from '@ember/template';
import { t } from 'ember-intl';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency, formatShortDate } from 'tio-ui/format';
import lastFourDigits from 'tio-common/helpers/last-four-digits';
import StatePill from 'tio-employee/components/tas/state-pill';
import { PhBooks, PhStudent, PhMoneyWavy, PhCreditCard } from 'ember-phosphor-icons';
import type { AmountInfo } from 'tio-employee/utils/tas/activity-helpers';
import type { ComponentLike } from '@glint/template';

interface ActivityCardSignature {
  Args: {
    /** Route to link to (e.g. "authenticated.tas.applications.show") */
    route: string;
    /** Model param for the route (the record id) */
    model: string | null;
    /** Display name for the card heading */
    name: string;
    /** Raw state string for the StatePill */
    state: string | undefined;
    /** Short ID for "ID# xxNNNN" display */
    shortId?: string | number | null;
    /** Program name to show in metadata line */
    programName?: string | null;
    /** Date string for "last action" display */
    updatedAt?: string | null;
    /** Institution name (extra metadata line, typically for program instances) */
    institution?: string | null;
    /** Amount info for reimbursement/course-cash metric pills */
    amountInfo?: AmountInfo | null;
    /** Card variant: "standalone" renders with border/shadow, "nested" renders as a flat row */
    variant?: 'standalone' | 'nested';
    /** Icon kind: "application" shows PhBooks, "program" shows PhStudent */
    iconKind?: 'application' | 'program';
    /** Optional payment state string (e.g. REFUND_REQUESTED) for a second badge */
    paymentState?: string | null;
    /** Number of courses (or credits for credit-based programs) to show as a metric pill */
    courseCount?: number | null;
    /** Whether the application is credit-based (swaps "Courses" label for "Credits") */
    isCreditBased?: boolean;
  };
  Blocks: {
    /** Optional block for custom right-side metrics (overrides default amountInfo pills) */
    metrics?: [];
  };
  Element: HTMLElement;
}

/**
 * ActivityCard — Shared card component for TAS activity items.
 *
 * Used on the dashboard, benefit-history, and getting-started pages to render
 * application and program instance cards with consistent layout and styling.
 *
 * @example Application card with amount info
 * ```gts
 * <ActivityCard
 *   @route="authenticated.tas.applications.show"
 *   @model={{app.id}}
 *   @name={{app.name}}
 *   @state={{app.state}}
 *   @shortId={{app.id}}
 *   @programName={{app.programName}}
 *   @updatedAt={{app.updatedAt}}
 *   @amountInfo={{this.amountInfo}}
 * />
 * ```
 *
 * @example Program instance with custom metrics block
 * ```gts
 * <ActivityCard
 *   @route="authenticated.tas.programs.show"
 *   @model={{program.id}}
 *   @name={{program.name}}
 *   @state={{program.state}}
 *   @shortId={{program.id}}
 *   @iconKind="program"
 * >
 *   <:metrics>
 *     <div>Custom metric content</div>
 *   </:metrics>
 * </ActivityCard>
 * ```
 */
export default class ActivityCard extends Component<ActivityCardSignature> {
  get isNested() {
    return this.args.variant === 'nested';
  }

  get containerClass() {
    if (this.isNested) {
      return 'tio-activity-card tio-activity-card--nested';
    }
    return 'tio-activity-card tio-activity-card--standalone';
  }

  get Icon(): ComponentLike<{ Element: SVGElement }> {
    return this.args.iconKind === 'program' ? PhStudent : PhBooks;
  }

  <template>
    <LinkTo @route={{@route}} @model={{@model}} class={{this.containerClass}}>
      <div class="tio-row">
        {{! Icon }}
        <div class="tio-row-center h-12 w-12 shrink-0 rounded-lg tio-bg-brand-soft">
          {{#let this.Icon as |Icon|}}
            <Icon class="h-7 w-7 tio-fg-brand" />
          {{/let}}
        </div>
        {{! Content }}
        <div class="tio-col-extra-tight tio-fill-min">
          <div class="tio-row-tight flex-wrap">
            <Heading @level={{3}} @size="base" class="truncate">{{@name}}</Heading>
            <StatePill @state={{@state}} @size="sm" />
            {{#if @paymentState}}
              <StatePill @state={{@paymentState}} @size="sm" />
            {{/if}}
          </div>
          <Text @variant="muted" @size="sm">
            {{#if @shortId}}
              <span>{{t
                  "tuition_assistance.reimagined.dashboard.activity_id"
                  id=(lastFourDigits @shortId)
                }}</span>
            {{/if}}
            {{#if @programName}}
              {{#if @shortId}}<span class="mx-1">&bull;</span>{{/if}}
              <span>{{@programName}}</span>
            {{/if}}
            {{#if (and (not (eq @courseCount null)) (not (eq @courseCount undefined)))}}
              {{#if (or @shortId @programName)}}<span class="mx-1">&bull;</span>{{/if}}
              <span>{{if
                  @isCreditBased
                  (t "tuition_assistance.reimagined.index.credit_count" count=@courseCount)
                  (t "tuition_assistance.reimagined.index.course_count" count=@courseCount)
                }}</span>
            {{/if}}
            {{#if @updatedAt}}
              {{#if (this.hasPrecedingMeta @shortId @programName @courseCount)}}
                <span class="mx-1">&bull;</span>
              {{/if}}
              <span>{{t
                  "tuition_assistance.reimagined.dashboard.activity_last_action"
                  date=(formatShortDate @updatedAt)
                }}</span>
            {{/if}}
          </Text>
          {{#if @institution}}
            <Text @variant="muted" @size="sm">
              {{@institution}}
            </Text>
          {{/if}}
        </div>
        {{! Right-side metrics }}
        {{#if (has-block "metrics")}}
          {{yield to="metrics"}}
        {{else if @amountInfo}}
          <div class="hidden shrink-0 gap-2 md:flex items-center">
            {{#if (gt @amountInfo.reimbursementAmount 0)}}
              <div class="rounded-md border tio-border-brand px-2 py-0.5 text-left leading-tight">
                <span class="tio-row-flush gap-0.5 tio-text-micro font-medium tio-fg-secondary">
                  {{t "tuition_assistance.reimagined.dashboard.metric_reimbursement"}}
                  <PhMoneyWavy class="tio-icon-sm" />
                </span>
                <div class="tio-text-xs-bold tio-fg-primary">
                  {{formatCurrency @amountInfo.reimbursementAmount}}
                </div>
                <span class="tio-text-micro tio-fg-brand">
                  {{t @amountInfo.reimbursementLabelKey}}
                </span>
              </div>
            {{/if}}
            {{#if @amountInfo.hasCourseCash}}
              <div class="rounded-md border tio-border-brand px-2 py-0.5 text-left leading-tight">
                <span class="tio-row-flush gap-0.5 tio-text-micro font-medium tio-fg-secondary">
                  {{htmlSafe (t "tuition_assistance.reimagined.dashboard.metric_course_cash")}}
                  <PhCreditCard class="tio-icon-sm" />
                </span>
                <div class="tio-text-xs-bold tio-fg-primary">
                  {{formatCurrency @amountInfo.courseCashAmount}}
                </div>
                <span class="tio-text-micro tio-fg-brand">
                  {{t @amountInfo.courseCashLabelKey}}
                </span>
              </div>
            {{/if}}
          </div>
        {{/if}}
      </div>
    </LinkTo>
  </template>

  /**
   * Returns true if there's a shortId or programName preceding the date,
   * meaning we need a bullet separator before the date.
   */
  hasPrecedingMeta = (
    shortId: string | number | null | undefined,
    programName: string | null | undefined,
    courseCount: number | null | undefined
  ) => {
    return !!shortId || !!programName || (courseCount !== null && courseCount !== undefined);
  };
}
